import type {
  GrexelStatementOptions,
  IndividualCancelStatement as SharedIndividualCancelStatement,
  PortfolioItemTransfer,
  TgeStatementOptions,
} from "@/types";
import type { RegistryEventSummary } from "../company_orders/transactionLogTypes";

/**
 * TypeScript interfaces for company order API types.
 * Based on Pydantic models from backend validators.
 */

// Company Order Enums
export enum OrderType {
  SEND = "SEND",
  RECEIVE = "RECEIVE",
  BUY = "BUY",
  SELL = "SELL",
  CANCEL = "CANCEL",
  BUY_AND_CANCEL = "BUY_AND_CANCEL",
}

export enum OrderStatus {
  WAITING_CONFIRMATION = "WAITING_CONFIRMATION",
  PENDING = "PENDING",
  PENDING_AGENT = "PENDING_AGENT",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
}

/**
 * Statuses in which an unpriced BUY / BUY_AND_CANCEL order is still waiting on the
 * desk's RFQ quote. Terminal and confirmed states are excluded — an unpriced order
 * there has no quote coming.
 */
export const isAwaitingRfqQuote = (
  status: OrderStatus | string | null | undefined
): boolean =>
  status === OrderStatus.WAITING_CONFIRMATION ||
  status === OrderStatus.PENDING ||
  status === OrderStatus.PENDING_AGENT ||
  status === OrderStatus.PROCESSING;

/**
 * @property date_from - Inclusive ISO 8601 instant on `created_at`
 * @property date_before - Exclusive ISO 8601 instant on `created_at`
 */
export interface CompanyOrderFiltersRequest {
  company_ids: number[];
  order_type?: OrderType | null;
  status?: OrderStatus | null;
  date_from?: string | null;
  date_before?: string | null;
  order_starter_name?: string | null;
}

export interface CompanyOrder {
  execution_method?: "REGISTRY_API" | "CNMC_FORMS";
  id: number;
  created_at: string;
  updated_at: string;
  order_type: OrderType;
  status: OrderStatus;
  order_starter_name: string | null;
  company_name?: string | null;
  company_id: number;
  account_id: number;
  processing_result?: string | null;
  total_selected_mwh: number;
  // Portfolio whose PPA action created the order (null/absent for manual orders).
  source_portfolio_id?: number;
  source_portfolio_name?: string | null;
  // Present on list/admin-list responses only; absent from confirm/cancel/update responses.
  order_details?: OrderDetails;
}

// Type mapping for order details based on order type
export type OrderDetailsMap = {
  [OrderType.SEND]: SendOrderDetails;
  [OrderType.RECEIVE]: ReceiveOrderDetails;
  [OrderType.BUY]: BuyOrderDetails | ClientPricedBuyOrderDetails;
  [OrderType.SELL]: SellOrderDetails;
  [OrderType.CANCEL]: CancelOrderDetails;
  [OrderType.BUY_AND_CANCEL]: BuyAndCancelOrderDetails;
};

// Union type of all possible order details
export type OrderDetails = OrderDetailsMap[OrderType];

/**
 * @property connected_events - The registry events connected to the order, in ascending occurrence;
 *   null when the order type is outside the connection scope or the company has no linked events
 */
export type CompanyOrderDetailsResponse<T extends OrderType = OrderType> =
  CompanyOrder & {
    order_details: OrderDetailsMap[T];
    connected_events?: RegistryEventSummary[] | null;
  };

export interface CompanyOrderListResponse {
  orders: CompanyOrder[];
  total_count: number;
}

export interface CompanyOrderConfirmResponse {
  order: CompanyOrder;
  message: string;
}

export interface CompanyOrderCancelResponse {
  order: CompanyOrder;
  message: string;
}

export type CompanyOrderUpdatePayload =
  | {
      // Either recipient_company_id (hosted) or recipient_eecs_account_number
      // (external) must be present; the backend's CompanyOrderSendRequest enforces
      // that at least one is set.
      recipient_eecs_account_number: string | null;
      recipient_registry_country?: string | null;
      recipient_company_id?: number | null;
      recipient_company_name: string;
      recipient_registry_internal_account_number?: string | null;
      unit_price_pln?: string | null;
      portfolio_items: PortfolioItemTransfer[];
    }
  | {
      statements: SharedIndividualCancelStatement[];
      portfolio_items: PortfolioItemTransfer[];
      global_operation_notes?: string | null;
      has_region_mismatch?: boolean;
    };

export interface CompanyOrderUpdateResponse {
  order: CompanyOrder;
  order_details: OrderDetails;
  message: string;
}

// Admin types
export interface AdminUpdateStatusRequest {
  status: "COMPLETED" | "FAILED" | "CANCELLED";
}

export interface AdminUpdateStatusResponse {
  id: number;
  status: string;
  updated_at: string;
  company_id: number;
  message: string;
}

// Order type-specific details interfaces based on spec
export interface SendOrderDetails {
  shape: "send";
  // Hosted sends persist a null EECS plus a recipient_company_id; external
  // sends keep the legacy EECS-only shape.
  recipient_eecs_account_number: string | null;
  recipient_company_id?: number | null;
  recipient_registry_country?: string;
  recipient_company_name: string;
  portfolio_items: PortfolioItemTransfer[];
  operation_notes?: string;
  recipient_registry_internal_account_number?: string | null;
  unit_price_pln?: string | null;
}

export interface ReceiveOrderDetails {
  shape: "receive";
  sender_eecs_account_number: string;
  sender_registry_country: string;
  sender_company_name: string;
  expected_mwh: number;
  operation_notes?: string;
}

export interface CancelOrderDetails {
  shape: "cancel";
  statements: IndividualCancelStatement[];
  portfolio_items: PortfolioItemTransfer[];
  global_operation_notes?: string | null;
  has_region_mismatch?: boolean;
}

export type SellStrategy =
  { type: "market" } | { type: "minimum"; minimumPrice: number };

export interface SellOrderDetails {
  shape: "sell";
  selected_portfolio_items: PortfolioItemTransfer[];
  strategy?: SellStrategy;
}

export interface BuyOrderDetails {
  shape: "buy";
  volume: number;
  country: string | null;
  purchase_mode: string;
  operation_notes: string | null;
  eac_source: string | null;
  technology: string | null;
  ecolabel: string | null;
  vintage: string | null;
  support: string | null;
  production_month: string | null;
  cod_requirement: string | null;
  hourly_matching: boolean;
  monthly_matching: boolean;
  custom_requirement: string | null;
  source_country: string | null;
  uploaded_file_id: number | null;
  analysis_form_response: Record<string, unknown> | null;
  /** Decimal string, exact as quoted. */
  quoted_total_price: string | null;
  pricing_request_id: string;
}

/** The quote the buy page computed in the browser before pricing moved to the server; prices are decimal strings. */
export interface ClientQuote {
  base_price: string;
  premiums: Record<string, string>;
  order_subtotal: string | null;
  minimum_service_fee: string | null;
  total_price: string;
}

/** A BUY order placed before server-side pricing: no pricing request, and the browser's quote if it had one. */
export interface ClientPricedBuyOrderDetails extends Omit<
  BuyOrderDetails,
  | "quoted_total_price"
  | "pricing_request_id"
  | "uploaded_file_id"
  | "analysis_form_response"
  | "shape"
> {
  shape: "client_priced_buy";
  client_quote: ClientQuote | null;
}

export interface BuyAndCancelOrderDetails {
  shape: "buy_and_cancel";
  volume: number;
  global_settings: any;
  cancellation_settings: any;
  beneficiary_name: string;
  registry_code?: string | null;
  residency: string;
  eac_type?: string | null;
  address: string;
  original_company_id: number;
  /** Prices are decimal strings, exact as quoted. */
  quoted_base_price: string | null;
  quoted_brokerage_fee: string | null;
  quoted_premiums: Record<string, string> | null;
  quoted_cancellation_fee: string | null;
  quoted_minimum_cancellation_fee: string | null;
  quoted_total_price: string | null;
  /** Correlation id of the pricing request this order was quoted under — the buyer-visible quote reference. */
  pricing_request_id?: string | null;
}

/**
 * Persisted/read shape of a cancellation statement (from the order-details JSON round-trip).
 *
 * Counterpart to the submit-side `IndividualCancelStatement` (`@/types`): same fields, but a few are
 * typed more loosely to match what the stored JSON actually round-trips (see inline notes). Keep the
 * shared fields in sync with the submit type — e.g. `is_any_aib` exists on both.
 */
export interface IndividualCancelStatement {
  temp_id?: string;
  beneficiary_name: string;
  beneficiary_type: string; // raw string from stored JSON, not the BeneficiaryTypeEnum
  registry_code: string | null; // always present on read (not optional)
  registry_country_region?: string | null;
  address: string | null; // always present on read (not optional)
  period_start?: string;
  period_end?: string;
  cancellation_purpose: string;
  mwh_cancelled: number | null;
  is_any_aib?: boolean;
  grexel_options?: GrexelStatementOptions | null; // null on read for public statements (no exclude_none)
  tge_options?: TgeStatementOptions | null;
  isCollapsed?: boolean;
}
